import { rem, useResponsive } from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Skeleton } from '@chakra-ui/react/skeleton';
import { Table } from '@chakra-ui/react/table';

import { BuildCacheToolIds } from '@/models/BuildCacheTool';
import { hidesCacheStatusColumn } from '@/pages/BuildCache/BuildCachePageWrapper/pages/Invocations/utils/invocationCacheStatus';

type InvocationListSkeletonProps = {
  isBuildSpecific: boolean;
  isProjectSpecific?: boolean;
  rows: number;
  selectedTool: BuildCacheToolIds;
};

const InvocationListSkeleton = ({
  isBuildSpecific,
  isProjectSpecific = false,
  rows,
  selectedTool,
}: InvocationListSkeletonProps) => {
  const { isMobile } = useResponsive();

  if (isMobile) {
    return (
      <InvocationListMobileSkeleton
        isBuildSpecific={isBuildSpecific}
        isProjectSpecific={isProjectSpecific}
        rows={rows}
        selectedTool={selectedTool}
      />
    );
  }

  return (
    <InvocationListTableDesktopSkeleton
      isBuildSpecific={isBuildSpecific}
      isProjectSpecific={isProjectSpecific}
      rows={rows}
      selectedTool={selectedTool}
    />
  );
};

const InvocationListTableDesktopSkeleton = ({
  isBuildSpecific,
  isProjectSpecific = false,
  rows,
  selectedTool,
}: InvocationListSkeletonProps) => {
  const hideProjectColumn = isBuildSpecific || isProjectSpecific;
  const isReactNative = selectedTool === BuildCacheToolIds.reactNative;
  const hideCacheStatusColumn = hidesCacheStatusColumn(selectedTool);
  return (
    <Table.ScrollArea aria-label="Loading...">
      <Table.Root aria-hidden>
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader width={rem(80)}>
              <Skeleton height="20" width="100%" />
            </Table.ColumnHeader>
            {!hideProjectColumn && (
              <Table.ColumnHeader>
                <Skeleton height="20" width="100%" />
              </Table.ColumnHeader>
            )}
            <Table.ColumnHeader>
              <Skeleton height="20" width="100%" />
            </Table.ColumnHeader>
            {isReactNative && (
              <Table.ColumnHeader width={rem(200)} minWidth={rem(200)}>
                <Skeleton height="20" width="100%" />
              </Table.ColumnHeader>
            )}
            {!hideCacheStatusColumn && (
              <Table.ColumnHeader width={rem(140)}>
                <Skeleton height="20" width="100%" />
              </Table.ColumnHeader>
            )}
            <Table.ColumnHeader width={rem(140)}>
              <Skeleton height="20" width="100%" />
            </Table.ColumnHeader>
            <Table.ColumnHeader width={rem(178)}>
              <Skeleton height="20" width="100%" />
            </Table.ColumnHeader>
            {!isBuildSpecific && <Table.ColumnHeader width={rem(80)} />}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {Array.from({ length: rows }).map((_, idx) => (
            <Table.Row key={`bone-${idx + 1}`}>
              <Table.Cell width={rem(80)}>
                <Skeleton height="20" width="20" />
              </Table.Cell>
              {!hideProjectColumn && (
                <Table.Cell>
                  <Skeleton height="20" width="100%" />
                </Table.Cell>
              )}
              <Table.Cell>
                <Skeleton height="20" width="100%" />
              </Table.Cell>
              {isReactNative && (
                <Table.Cell width={rem(200)}>
                  <Skeleton height="20" width="100%" />
                </Table.Cell>
              )}
              {!hideCacheStatusColumn && (
                <Table.Cell width={rem(140)}>
                  <Skeleton height="20" width="100%" />
                </Table.Cell>
              )}
              <Table.Cell width={rem(140)}>
                <Skeleton height="20" width="100%" />
              </Table.Cell>
              <Table.Cell width={rem(178)}>
                <Skeleton height="20" width="100%" />
              </Table.Cell>
              {!isBuildSpecific && <Table.Cell width={rem(80)} />}
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
    </Table.ScrollArea>
  );
};

const InvocationListMobileSkeleton = ({
  isBuildSpecific,
  isProjectSpecific = false,
  rows,
  selectedTool,
}: InvocationListSkeletonProps) => {
  const hideProjectInfo = isBuildSpecific || isProjectSpecific;
  // The card shows Target per invocation, for the ones that report a targetOs, which nothing knows
  // before the response lands. The tab is the closest the skeleton can get.
  const showTargetRow = selectedTool === BuildCacheToolIds.reactNative;
  return (
    <Box aria-label="Loading...">
      {/* Sort select */}
      <Skeleton marginBottom="16" height="40" width="100%" />
      {/* Cards */}
      <Box display="flex" flexDirection="column" gap="1.5rem" aria-hidden>
        {Array.from({ length: rows }).map((_, idx) => (
          <Table.ScrollArea key={`card-${idx + 1}`}>
            <Table.Root>
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader>
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap="1rem">
                      <Box display="flex" alignItems="center" gap=".5rem">
                        <Skeleton height="16" width="16" />
                        <Skeleton height="20" width={rem(160)} />
                      </Box>
                      <Box display="flex" gap=".5rem">
                        <Skeleton height="32" width="32" />
                        <Skeleton height="32" width="32" />
                      </Box>
                    </Box>
                  </Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                <Table.Row>
                  <Table.Cell>
                    <Box display="flex" flexDirection="column" gap=".5rem" paddingY=".5rem">
                      {!hideProjectInfo && (
                        <Skeleton data-testid="invocationCardRowSkeleton" height="20" width="100%" />
                      )}
                      {/* The card's Command label and value are siblings of this column, not one row of it. */}
                      <Skeleton data-testid="invocationCardRowSkeleton" height="20" width="100%" />
                      <Skeleton data-testid="invocationCardRowSkeleton" height="20" width="100%" />
                      {showTargetRow && <Skeleton data-testid="invocationCardRowSkeleton" height="20" width="100%" />}
                      {!isBuildSpecific && (
                        <Skeleton data-testid="invocationCardRowSkeleton" height="20" width={rem(56)} />
                      )}
                    </Box>
                  </Table.Cell>
                </Table.Row>
              </Table.Body>
            </Table.Root>
          </Table.ScrollArea>
        ))}
      </Box>
    </Box>
  );
};

export default InvocationListSkeleton;
